import React, { useState, useEffect } from 'react';
import { Search, Film, Tv, X, Loader } from 'lucide-react';
import { API_CONFIG } from './config/apiConfig';

const getHeaders = () => ({
    'Content-Type': 'application/json',
    'X-Admin-Token': localStorage.getItem('cinestream_token') || ''
});

const MovieRequestForm = ({ user, onClose }) => {
    const [contentType, setContentType] = useState('film'); // 'film' ou 'serie'
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedMovie, setSelectedMovie] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);

    const [formData, setFormData] = useState({
        title: '',
        year: '',
        genre: '',
        comment: ''
    });

    const [myRequests, setMyRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [activeTab, setActiveTab] = useState('form');

    // Charger les demandes au montage
    useEffect(() => {
        loadMyRequests();
    }, []);

    // Recharger quand on change d'onglet
    useEffect(() => {
        if (activeTab === 'myRequests') {
            loadMyRequests();
        }
    }, [activeTab]);

    // Recherche TMDB avec debounce
    useEffect(() => {
        if (searchQuery.length < 2) {
            setSearchResults([]);
            setShowDropdown(false);
            return;
        }

        const timer = setTimeout(() => {
            searchTMDB(searchQuery);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery, contentType]);

    const searchTMDB = async (query) => {
        setIsSearching(true);
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/tmdb/search?query=${encodeURIComponent(query)}&type=${contentType === 'serie' ? 'tv' : 'movie'}`, {
                headers: getHeaders()
            });
            const data = await response.json();

            if (data.success) {
                setSearchResults(data.results);
                setShowDropdown(true);
            }
        } catch (err) {
            console.error('Erreur recherche TMDB:', err);
        } finally {
            setIsSearching(false);
        }
    };

    const loadMyRequests = async () => {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/movie-requests?user_id=${user.id}`, {
                headers: getHeaders()
            });
            const data = await response.json();
            if (data.success) {
                setMyRequests(data.requests);
            }
        } catch (err) {
            console.error('Erreur lors du chargement des demandes:', err);
        }
    };

    const handleSelectMovie = (movie) => {
        setSelectedMovie(movie);
        setFormData({
            title: movie.title,
            year: movie.year || '',
            genre: '',
            comment: formData.comment
        });
        setSearchQuery(movie.title);
        setShowDropdown(false);
    };

    const handleClearSelection = () => {
        setSelectedMovie(null);
        setSearchQuery('');
        setFormData({
            title: '',
            year: '',
            genre: '',
            comment: formData.comment
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        if (!formData.title.trim()) {
            setError('Le titre est requis');
            setLoading(false);
            return;
        }

        try {
            const requestData = {
                user_id: user.id,
                username: user.username,
                content_type: contentType,
                tmdb_id: selectedMovie?.id || null,
                ...formData
            };

            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/movie-requests`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify(requestData)
            });

            const data = await response.json();

            if (data.success) {
                setSuccess(`Ta demande a été envoyée ! 🎬${data.discord_sent ? ' Notification Discord envoyée !' : ''}`);
                setFormData({ title: '', year: '', genre: '', comment: '' });
                setSelectedMovie(null);
                setSearchQuery('');

                setTimeout(() => {
                    setSuccess('');
                    setActiveTab('myRequests');
                }, 2000);
            } else {
                setError(data.message || 'Erreur lors de l\'envoi');
            }
        } catch (err) {
            setError('Erreur de connexion au serveur');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            pending: { text: 'En attente', color: 'bg-yellow-500' },
            processing: { text: 'En cours', color: 'bg-blue-500' },
            completed: { text: 'Ajouté ✅', color: 'bg-green-500' },
            rejected: { text: 'Refusé ❌', color: 'bg-red-500' }
        };
        const badge = badges[status] || badges.pending;
        return (
            <span className={`${badge.color} text-white px-3 py-1 rounded-full text-sm font-semibold`}>
                {badge.text}
            </span>
        );
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const inputStyle = {
        width: '100%', background: 'var(--glass)', border: '1px solid var(--glass-border)',
        borderRadius: 'var(--r-md)', padding: '10px 14px', color: 'var(--text-1)',
        fontSize: '0.9375rem', outline: 'none', transition: 'border-color 200ms ease, box-shadow 200ms ease',
        boxSizing: 'border-box',
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
            <div style={{ background: 'var(--bg-modal)', borderRadius: 'var(--r-2xl)', maxWidth: 680, width: '100%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', border: '1px solid var(--glass-border)', boxShadow: 'var(--shadow-xl)' }}>
                {/* Header */}
                <div style={{ position: 'sticky', top: 0, background: 'var(--bg-modal)', borderBottom: '1px solid var(--glass-border)', padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 10, flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ width: 42, height: 42, borderRadius: 12, background: 'linear-gradient(135deg, var(--accent-deep), var(--accent))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Film style={{ width: 20, height: 20, color: '#fff' }} />
                        </div>
                        <div>
                            <h2 style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '1.125rem' }}>Demande de contenu</h2>
                            <p style={{ color: 'var(--text-4)', fontSize: '0.8125rem' }}>Suggère un film ou une série</p>
                        </div>
                    </div>
                    <button onClick={onClose} style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--glass)', border: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', cursor: 'pointer', transition: 'all 200ms ease' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--glass-hover)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'var(--glass)'}
                    >
                        <X style={{ width: 16, height: 16 }} />
                    </button>
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', padding: '0 24px', borderBottom: '1px solid var(--glass-border)', flexShrink: 0 }}>
                    {[
                        { key: 'form', label: 'Nouvelle demande' },
                        { key: 'myRequests', label: `Mes demandes (${myRequests.length})` },
                    ].map(tab => (
                        <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                            style={{ display: 'inline-flex', alignItems: 'center', padding: '14px 16px', background: 'none', border: 'none', color: activeTab === tab.key ? 'var(--text-1)' : 'var(--text-4)', fontWeight: activeTab === tab.key ? 600 : 400, fontSize: '0.9375rem', cursor: 'pointer', borderBottom: `2px solid ${activeTab === tab.key ? 'var(--accent)' : 'transparent'}`, marginBottom: -1, transition: 'all 150ms ease' }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
                    {activeTab === 'form' ? (
                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                            {error && <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 'var(--r-md)', background: 'var(--error-bg)', border: '1px solid rgba(239,68,68,0.3)', color: '#FCA5A5', fontSize: '0.875rem' }}>{error}</div>}
                            {success && <div style={{ padding: '12px 16px', borderRadius: 'var(--r-md)', background: 'var(--success-bg)', border: '1px solid rgba(16,185,129,0.3)', color: '#6EE7B7', fontSize: '0.875rem' }}>{success}</div>}

                            {/* Type selector */}
                            <div>
                                <label style={{ display: 'block', color: 'var(--text-2)', fontWeight: 600, fontSize: '0.875rem', marginBottom: 10 }}>Type de contenu</label>
                                <div style={{ display: 'flex', gap: 10 }}>
                                    {[
                                        { type: 'film', label: 'Film', icon: <Film style={{ width: 18, height: 18 }} /> },
                                        { type: 'serie', label: 'Série', icon: <Tv style={{ width: 18, height: 18 }} /> },
                                    ].map(({ type, label, icon }) => (
                                        <button key={type} type="button"
                                            onClick={() => { setContentType(type); setSelectedMovie(null); setSearchQuery(''); setSearchResults([]); }}
                                            style={{
                                                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                                padding: '12px 16px', borderRadius: 'var(--r-md)',
                                                background: contentType === type ? (type === 'film' ? 'rgba(232,183,90,0.2)' : 'rgba(59,130,246,0.2)') : 'var(--glass)',
                                                border: `1px solid ${contentType === type ? (type === 'film' ? 'rgba(232,183,90,0.4)' : 'rgba(59,130,246,0.4)') : 'var(--glass-border)'}`,
                                                color: contentType === type ? (type === 'film' ? 'var(--accent-bright)' : '#93C5FD') : 'var(--text-3)',
                                                fontWeight: contentType === type ? 700 : 500, cursor: 'pointer', transition: 'all 200ms ease', fontSize: '0.9375rem',
                                            }}
                                        >
                                            {icon}{label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* TMDB Search */}
                            <div style={{ position: 'relative' }}>
                                <label style={{ display: 'block', color: 'var(--text-2)', fontWeight: 600, fontSize: '0.875rem', marginBottom: 8 }}>Rechercher sur TMDB</label>
                                <div style={{ position: 'relative' }}>
                                    <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-4)' }}>
                                        {isSearching
                                            ? <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid var(--accent-glow)', borderTop: '2px solid var(--accent)', animation: 'spinSlow 0.8s linear infinite' }} />
                                            : <Search style={{ width: 18, height: 18 }} />
                                        }
                                    </div>
                                    <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                                        placeholder={`Chercher un ${contentType === 'serie' ? 'série' : 'film'}...`}
                                        disabled={loading}
                                        style={{ ...inputStyle, paddingLeft: 44, paddingRight: searchQuery || selectedMovie ? 44 : 14 }}
                                        onFocus={e => { e.target.style.borderColor = 'var(--accent-ring)'; e.target.style.boxShadow = '0 0 0 3px var(--accent-glow)'; }}
                                        onBlur={e => { e.target.style.borderColor = 'var(--glass-border)'; e.target.style.boxShadow = 'none'; }}
                                    />
                                    {(searchQuery || selectedMovie) && (
                                        <button type="button" onClick={handleClearSelection}
                                            style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer' }}
                                        >
                                            <X style={{ width: 18, height: 18 }} />
                                        </button>
                                    )}
                                </div>

                                {showDropdown && searchResults.length > 0 && (
                                    <div style={{ position: 'absolute', zIndex: 50, width: '100%', marginTop: 6, background: 'var(--bg-modal)', border: '1px solid var(--glass-border)', borderRadius: 'var(--r-lg)', boxShadow: 'var(--shadow-xl)', maxHeight: 320, overflowY: 'auto' }}>
                                        {searchResults.map(result => (
                                            <button key={result.id} type="button" onClick={() => handleSelectMovie(result)}
                                                style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px', background: 'none', border: 'none', borderBottom: '1px solid var(--glass-border)', cursor: 'pointer', transition: 'background 150ms ease', textAlign: 'left' }}
                                                onMouseEnter={e => e.currentTarget.style.background = 'var(--glass)'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                            >
                                                <div style={{ flexShrink: 0, width: 40, height: 54, borderRadius: 6, overflow: 'hidden', background: 'var(--bg-elevated)' }}>
                                                    {result.posterPath
                                                        ? <img src={result.posterPath} alt={result.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Film style={{ width: 18, height: 18, color: 'var(--text-4)' }} /></div>
                                                    }
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <p style={{ color: 'var(--text-1)', fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{result.title}</p>
                                                    <p style={{ color: 'var(--text-4)', fontSize: '0.75rem' }}>{result.year}{result.voteAverage > 0 ? ` · ⭐ ${result.voteAverage.toFixed(1)}` : ''}</p>
                                                    {result.overview && <p style={{ color: 'var(--text-4)', fontSize: '0.6875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{result.overview.substring(0, 80)}...</p>}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {selectedMovie && !showDropdown && (
                                    <div style={{ marginTop: 8, padding: '12px 14px', borderRadius: 'var(--r-md)', background: 'linear-gradient(135deg, rgba(232,183,90,0.12), rgba(232,183,90,0.06))', border: '1px solid rgba(232,183,90,0.25)', display: 'flex', gap: 12 }}>
                                        <div style={{ flexShrink: 0, width: 44, height: 60, borderRadius: 6, overflow: 'hidden', background: 'var(--bg-elevated)' }}>
                                            {selectedMovie.posterPath
                                                ? <img src={selectedMovie.posterPath} alt={selectedMovie.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Film style={{ width: 20, height: 20, color: 'var(--text-4)' }} /></div>
                                            }
                                        </div>
                                        <div>
                                            <p style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '0.9375rem' }}>{selectedMovie.title}</p>
                                            <p style={{ color: 'var(--text-3)', fontSize: '0.8125rem' }}>{selectedMovie.year}</p>
                                            {selectedMovie.voteAverage > 0 && <p style={{ color: '#FCD34D', fontSize: '0.8125rem', marginTop: 2 }}>⭐ {selectedMovie.voteAverage.toFixed(1)}/10</p>}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div style={{ textAlign: 'center', color: 'var(--text-4)', fontSize: '0.8125rem', position: 'relative' }}>
                                <span style={{ padding: '0 12px', background: 'var(--bg-modal)', position: 'relative', zIndex: 1 }}>ou saisie manuelle</span>
                                <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 1, background: 'var(--glass-border)' }} />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div>
                                    <label style={{ display: 'block', color: 'var(--text-2)', fontWeight: 600, fontSize: '0.8125rem', marginBottom: 6 }}>Titre</label>
                                    <input type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="Ex: Inception" style={inputStyle}
                                        onFocus={e => { e.target.style.borderColor = 'var(--accent-ring)'; e.target.style.boxShadow = '0 0 0 3px var(--accent-glow)'; }}
                                        onBlur={e => { e.target.style.borderColor = 'var(--glass-border)'; e.target.style.boxShadow = 'none'; }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', color: 'var(--text-2)', fontWeight: 600, fontSize: '0.8125rem', marginBottom: 6 }}>Année</label>
                                    <input type="text" value={formData.year} onChange={e => setFormData({ ...formData, year: e.target.value })} placeholder="2010" style={inputStyle}
                                        onFocus={e => { e.target.style.borderColor = 'var(--accent-ring)'; e.target.style.boxShadow = '0 0 0 3px var(--accent-glow)'; }}
                                        onBlur={e => { e.target.style.borderColor = 'var(--glass-border)'; e.target.style.boxShadow = 'none'; }}
                                    />
                                </div>
                            </div>

                            <div>
                                <label style={{ display: 'block', color: 'var(--text-2)', fontWeight: 600, fontSize: '0.8125rem', marginBottom: 6 }}>Genre (optionnel)</label>
                                <input type="text" value={formData.genre} onChange={e => setFormData({ ...formData, genre: e.target.value })} placeholder="Action, Sci-Fi" style={inputStyle}
                                    onFocus={e => { e.target.style.borderColor = 'var(--accent-ring)'; e.target.style.boxShadow = '0 0 0 3px var(--accent-glow)'; }}
                                    onBlur={e => { e.target.style.borderColor = 'var(--glass-border)'; e.target.style.boxShadow = 'none'; }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', color: 'var(--text-2)', fontWeight: 600, fontSize: '0.8125rem', marginBottom: 6 }}>Pourquoi ce contenu ? (optionnel)</label>
                                <textarea value={formData.comment} onChange={e => setFormData({ ...formData, comment: e.target.value })} rows={3}
                                    placeholder={`J'aimerais voir ${contentType === 'serie' ? 'cette série' : 'ce film'} parce que...`}
                                    style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
                                    onFocus={e => { e.target.style.borderColor = 'var(--accent-ring)'; e.target.style.boxShadow = '0 0 0 3px var(--accent-glow)'; }}
                                    onBlur={e => { e.target.style.borderColor = 'var(--glass-border)'; e.target.style.boxShadow = 'none'; }}
                                />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
                                <button type="button" onClick={onClose} className="btn-ghost" disabled={loading} style={{ padding: '12px 20px', fontSize: '0.9375rem' }}>
                                    Annuler
                                </button>
                                <button type="submit" disabled={loading || !formData.title.trim()} className="btn-primary"
                                    style={{ padding: '12px 20px', fontSize: '0.9375rem', display: 'flex', alignItems: 'center', gap: 8, opacity: (loading || !formData.title.trim()) ? 0.5 : 1, cursor: (loading || !formData.title.trim()) ? 'not-allowed' : 'pointer' }}
                                >
                                    {loading && <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', animation: 'spinSlow 0.8s linear infinite' }} />}
                                    {loading ? 'Envoi en cours...' : 'Envoyer la demande'}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {myRequests.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '60px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                                    <Film style={{ width: 48, height: 48, color: 'var(--text-4)' }} />
                                    <p style={{ color: 'var(--text-2)', fontWeight: 600 }}>Aucune demande pour l'instant</p>
                                    <p style={{ color: 'var(--text-4)', fontSize: '0.875rem' }}>Soumets ta première demande dans l'onglet précédent</p>
                                </div>
                            ) : (
                                myRequests.map(request => (
                                    <div key={request.id} style={{ padding: '14px 16px', borderRadius: 'var(--r-lg)', background: 'var(--glass)', border: '1px solid var(--glass-border)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                {request.content_type === 'serie'
                                                    ? <Tv style={{ width: 16, height: 16, color: '#93C5FD', flexShrink: 0 }} />
                                                    : <Film style={{ width: 16, height: 16, color: 'var(--accent-bright)', flexShrink: 0 }} />
                                                }
                                                <h3 style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '0.9375rem' }}>
                                                    {request.title}
                                                    {request.year && <span style={{ color: 'var(--text-4)', fontWeight: 400, marginLeft: 6 }}>({request.year})</span>}
                                                </h3>
                                            </div>
                                            {getStatusBadge(request.status)}
                                        </div>
                                        {request.comment && <p style={{ color: 'var(--text-3)', fontSize: '0.8125rem', fontStyle: 'italic', marginBottom: 8 }}>"{request.comment}"</p>}
                                        {request.admin_note && request.status !== 'pending' && (
                                            <div style={{ padding: '10px 12px', borderRadius: 'var(--r-sm)', background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)', marginBottom: 8 }}>
                                                <p style={{ color: 'var(--text-4)', fontSize: '0.75rem', fontWeight: 600, marginBottom: 4 }}>Note admin :</p>
                                                <p style={{ color: 'var(--text-2)', fontSize: '0.8125rem' }}>{request.admin_note}</p>
                                            </div>
                                        )}
                                        <p style={{ color: 'var(--text-4)', fontSize: '0.75rem' }}>Demandé le {formatDate(request.created_at)}</p>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MovieRequestForm;